import type { PersonSubmissionIndex } from '@cognitoforms/types/server-types/core/model/forms/person-submission-index';
import type { PersonSubmissionFilterCriteria } from '@cognitoforms/types/server-types/core/person-submission-filter-criteria';
import type { PersonSubmissionSummary } from '@cognitoforms/types/server-types/core/services/forms/person-submission-summary';
import type { CustomerAvatarInfo } from '@cognitoforms/types/server-types/core/services/forms/customer-avatar-info';
import type { PersonInfo } from '@cognitoforms/types/server-types/core/services/forms/person-info';
import type { ResolveEntryViewContainingEntryResult } from '@cognitoforms/types/server-types/forms/services/resolve-entry-view-containing-entry-result';
import type { TablePageResult } from './query-table-page';
import { serviceRequest, isServiceRequestError } from './service-request';
import { transformDateRangeFilter } from '@cognitoforms/utils/date-helpers';

export type { PersonSubmissionIndex, PersonSubmissionFilterCriteria, PersonSubmissionSummary, PersonInfo, ResolveEntryViewContainingEntryResult };

export type PersonSubmissionTableData = {
	FilterOptions: PersonSubmissionSummary;
	Page: TablePageResult<PersonSubmissionIndex>;
};

/**
 * Queries entries linked to a person entry with optional filtering and pagination.
 * Uses the PersonSubmissionIndex for efficient querying with denormalized data.
 */
export function queryPersonSubmissions(
	personEntryId: string,
	pageSize = 100,
	continuationToken?: string,
	filter?: Partial<PersonSubmissionFilterCriteria>
): Promise<TablePageResult<PersonSubmissionIndex>> {
	const normalizedFilter = transformDateRangeFilter(filter);
	return serviceRequest(
		`svc/person-submissions/${personEntryId}/query-page`,
		{ pageSize, continuationToken: continuationToken ?? null, filter: normalizedFilter ?? null },
		{ method: 'POST' }
	);
}

/**
 * Gets summary information for a person's linked entries.
 * Used to populate filter options and show aggregate statistics.
 */
export function getPersonSubmissionSummary(personEntryId: string): Promise<PersonSubmissionSummary> {
	return serviceRequest(`svc/person-submissions/${personEntryId}/summary`, null, { method: 'GET' });
}

/**
 * Fetches the first page of submissions and the summary in a single round trip
 */
export function getPersonSubmissionsTableData(personEntryId: string, signal?: AbortSignal): Promise<PersonSubmissionTableData> {
	return serviceRequest(`svc/person-submissions/${personEntryId}/table-data`, null, { method: 'GET', signal });
}

/**
 * Resolves the tracked person an entry is linked to.
 * Returns null when the entry has no linked person to show.
 */
export function getLinkedPerson(sourceEntryId: string): Promise<PersonInfo | null> {
	return serviceRequest(`svc/person-submissions/${sourceEntryId}/linked-person`, null, { method: 'GET' });
}

/**
 * Resolves an accessible entry view that actually contains the given entry for the current user.
 * Returns null (404) when the entry is not accessible in any view.
 */
export async function resolveEntryViewContainingEntry(formId: string, entryId: string): Promise<ResolveEntryViewContainingEntryResult | null> {
	try {
		return await serviceRequest(`svc/forms/${formId}/entry-views/entry/${entryId}/resolve-entry-view-containing-entry`, null, { method: 'GET' });
	}
	catch (err) {
		if (isServiceRequestError(err) && err.statusCode === 404)
			return null;
		throw err;
	}
}

export function getCustomerAvatarInfo(entryIds: string[]): Promise<Record<string, CustomerAvatarInfo>> {
	const params = new URLSearchParams();
	entryIds.forEach(id => params.append('entryIds', id));
	return serviceRequest(`svc/person-submissions/customer-avatars?${params.toString()}`, null, { method: 'GET' });
}
